import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/client';
import type { Project, Measurement, MeasurementItem } from '../types';

const UNITS = ['m²', 'lfm', 'Stk', 'pauschal'] as const;

const unitColors: Record<string, string> = {
  'm²': 'bg-blue-50 text-blue-700 border-blue-200',
  'lfm': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Stk': 'bg-purple-50 text-purple-700 border-purple-200',
  'pauschal': 'bg-amber-50 text-amber-700 border-amber-200',
};

function safeEvalFormula(formula: string): number {
  // Allow only numbers, spaces, decimal points/commas, and basic math operators
  const sanitized = formula.replace(/,/g, '.').trim();
  if (!/^[\d\s.+\-*/()]+$/.test(sanitized)) return 0;
  try {
    const result = new Function(`"use strict"; return (${sanitized})`)();
    return typeof result === 'number' && isFinite(result) ? Math.round(result * 1000) / 1000 : 0;
  } catch {
    return 0;
  }
}

interface FormState {
  id?: string;
  projectId: string;
  title: string;
  unit: string;
  items: MeasurementItem[];
  notes: string;
}

const emptyForm: FormState = {
  projectId: '',
  title: '',
  unit: 'm²',
  items: [{ description: '', formula: '', result: 0 }],
  notes: '',
};

export default function MeasurementsPage() {
  const qc = useQueryClient();
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [form, setForm] = useState<FormState | null>(null);

  const { data: projects = [] } = useQuery<Project[]>({
    queryKey: ['projects'],
    queryFn: () => api.get('/projects').then((r) => r.data),
  });

  const { data: measurements = [], isLoading } = useQuery<Measurement[]>({
    queryKey: ['measurements', selectedProject],
    queryFn: () =>
      api.get(`/measurements${selectedProject ? `?projectId=${selectedProject}` : ''}`).then((r) => r.data),
  });

  // Group measurements by project
  const grouped = useMemo(() => {
    const map = new Map<string, { projectTitle: string; items: Measurement[] }>();
    for (const m of measurements) {
      const key = m.projectId;
      if (!map.has(key)) {
        map.set(key, { projectTitle: m.project?.title || 'Unbekanntes Projekt', items: [] });
      }
      map.get(key)!.items.push(m);
    }
    return Array.from(map.entries());
  }, [measurements]);

  const saveMutation = useMutation({
    mutationFn: (data: FormState) => {
      const totalQuantity = data.items.reduce((sum, i) => sum + i.result, 0);
      const payload = {
        projectId: data.projectId,
        title: data.title,
        unit: data.unit,
        items: data.items.filter((i) => i.description || i.formula),
        totalQuantity: Math.round(totalQuantity * 1000) / 1000,
        notes: data.notes || undefined,
      };
      if (data.id) {
        return api.put(`/measurements/${data.id}`, payload);
      }
      return api.post('/measurements', payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['measurements'] });
      setForm(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/measurements/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['measurements'] });
    },
  });

  function openCreateForm() {
    setForm({ ...emptyForm, projectId: selectedProject || '' });
  }

  function openEditForm(m: Measurement) {
    setForm({
      id: m.id,
      projectId: m.projectId,
      title: m.title,
      unit: m.unit,
      items: m.items.length > 0 ? [...m.items] : [{ description: '', formula: '', result: 0 }],
      notes: m.notes || '',
    });
  }

  function updateItem(index: number, field: keyof MeasurementItem, value: string) {
    if (!form) return;
    const newItems = [...form.items];
    if (field === 'formula') {
      newItems[index] = { ...newItems[index], formula: value, result: safeEvalFormula(value) };
    } else {
      newItems[index] = { ...newItems[index], [field]: value };
    }
    setForm({ ...form, items: newItems });
  }

  function addItem() {
    if (!form) return;
    setForm({ ...form, items: [...form.items, { description: '', formula: '', result: 0 }] });
  }

  function removeItem(index: number) {
    if (!form) return;
    const newItems = form.items.filter((_, i) => i !== index);
    setForm({ ...form, items: newItems.length > 0 ? newItems : [{ description: '', formula: '', result: 0 }] });
  }

  const formTotal = form ? form.items.reduce((sum, i) => sum + i.result, 0) : 0;
  const fmt = (n: number) => Number(n).toFixed(3).replace('.', ',');
  const totalAll = measurements.reduce((sum, m) => sum + Number(m.totalQuantity), 0);

  return (
    <div>
      <div className="sp-page-header">
        <div>
          <h1 className="sp-page-title">Aufmaß</h1>
          <p className="sp-page-subtitle">Baustellen-Messungen erfassen und verwalten</p>
        </div>
        <button onClick={openCreateForm} className="sp-btn-primary">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Neues Aufmaß
        </button>
      </div>

      {/* Filter + Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="col-span-2">
          <label className="sp-label">Projekt filtern</label>
          <select
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
            className="sp-input"
          >
            <option value="">Alle Projekte</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>{p.title}</option>
            ))}
          </select>
        </div>
        <div className="sp-stat-card">
          <span className="sp-stat-label">Aufmaße gesamt</span>
          <span className="sp-stat-value">{measurements.length}</span>
        </div>
        <div className="sp-stat-card">
          <span className="sp-stat-label">Gesamtmenge</span>
          <span className="sp-stat-value">{fmt(totalAll)}</span>
        </div>
      </div>

      {/* Create/Edit Form */}
      {form && (
        <div className="sp-form-card mb-6 animate-fade-in">
          <h3 className="text-sm font-semibold text-slate-800 mb-4">
            {form.id ? 'Aufmaß bearbeiten' : 'Neues Aufmaß anlegen'}
          </h3>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <label className="sp-label">Projekt</label>
              <select
                value={form.projectId}
                onChange={(e) => setForm({ ...form, projectId: e.target.value })}
                className="sp-input"
                disabled={!!form.id}
              >
                <option value="">Projekt wählen...</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.title}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="sp-label">Bezeichnung</label>
              <input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="sp-input"
                placeholder="z.B. Bodenfliesen Erdgeschoss"
                autoFocus
              />
            </div>
            <div>
              <label className="sp-label">Einheit</label>
              <select
                value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}
                className="sp-input"
              >
                {UNITS.map((u) => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Items */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="sp-label !mb-0">Positionen</label>
              <button onClick={addItem} className="sp-btn-secondary !py-1 !px-2.5 !text-xs">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Zeile hinzufügen
              </button>
            </div>
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="text-left px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider w-5">#</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Beschreibung</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Formel</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider w-28">Ergebnis</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {form.items.map((item, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-3 py-2 text-xs text-slate-400">{i + 1}</td>
                      <td className="px-3 py-1">
                        <input
                          value={item.description}
                          onChange={(e) => updateItem(i, 'description', e.target.value)}
                          className="sp-input !py-1.5 !text-sm"
                          placeholder="z.B. Küche Länge x Breite"
                        />
                      </td>
                      <td className="px-3 py-1">
                        <input
                          value={item.formula}
                          onChange={(e) => updateItem(i, 'formula', e.target.value)}
                          className="sp-input !py-1.5 !text-sm font-mono"
                          placeholder="z.B. 4,5 * 3,2"
                        />
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-sm font-semibold text-slate-800">
                        {item.result.toFixed(3).replace('.', ',')}
                      </td>
                      <td className="px-2 py-2">
                        <button
                          onClick={() => removeItem(i)}
                          className="sp-btn-icon !w-7 !h-7 hover:!text-error-500 hover:!bg-error-50"
                          title="Zeile entfernen"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                  {/* Total row */}
                  <tr className="border-t-2 border-slate-200 bg-slate-50">
                    <td></td>
                    <td className="px-3 py-2 text-xs font-semibold text-slate-600 uppercase">Summe</td>
                    <td></td>
                    <td className="px-3 py-2 text-right font-mono text-sm font-bold text-brand-600">
                      {formTotal.toFixed(3).replace('.', ',')} {form.unit}
                    </td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Notes */}
          <div className="mb-4">
            <label className="sp-label">Notizen</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="sp-input !h-20 resize-none"
              placeholder="Optionale Anmerkungen..."
            />
          </div>

          <div className="sp-form-actions">
            <button
              onClick={() => saveMutation.mutate(form)}
              disabled={saveMutation.isPending || !form.projectId || !form.title}
              className="sp-btn-primary"
            >
              {saveMutation.isPending ? 'Speichern...' : form.id ? 'Aktualisieren' : 'Speichern'}
            </button>
            <button onClick={() => setForm(null)} className="sp-btn-secondary">Abbrechen</button>
          </div>
        </div>
      )}

      {/* Measurements List */}
      {isLoading ? (
        <div className="sp-card p-12 text-center">
          <span className="text-sm text-slate-400">Laden...</span>
        </div>
      ) : measurements.length === 0 ? (
        <div className="sp-card p-12">
          <div className="sp-empty">
            <svg className="w-12 h-12 text-slate-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2-1-2 1M4 7l2-1M4 7l2 1M4 7v2.5M12 21l-2-1m2 1l2-1m-2 1v-2.5M6 18l-2-1v-2.5M18 18l2-1v-2.5" />
            </svg>
            <span className="text-sm font-medium text-slate-500">Noch keine Aufmaße vorhanden</span>
            <p className="text-xs text-slate-400 mt-1">Erstellen Sie Ihr erstes Aufmaß mit dem Button oben.</p>
          </div>
        </div>
      ) : (
        grouped.map(([projectId, group]) => (
          <div key={projectId} className="mb-6 animate-fade-in">
            <h2 className="text-sm font-semibold text-slate-600 mb-3 flex items-center gap-2">
              <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              {group.projectTitle}
            </h2>
            <div className="sp-card overflow-hidden">
              <table className="sp-table">
                <thead>
                  <tr>
                    <th>Bezeichnung</th>
                    <th>Einheit</th>
                    <th className="text-right">Positionen</th>
                    <th className="text-right">Gesamtmenge</th>
                    <th>Notizen</th>
                    <th>Erstellt</th>
                    <th className="w-24"></th>
                  </tr>
                </thead>
                <tbody>
                  {group.items.map((m) => (
                    <tr key={m.id}>
                      <td className="font-medium text-slate-800">{m.title}</td>
                      <td>
                        <span className={`sp-badge border text-[10px] ${unitColors[m.unit] || 'bg-slate-50 text-slate-700 border-slate-200'}`}>
                          {m.unit}
                        </span>
                      </td>
                      <td className="text-right text-slate-500">{m.items.length}</td>
                      <td className="text-right font-mono text-xs font-semibold text-slate-800">
                        {fmt(Number(m.totalQuantity))} {m.unit}
                      </td>
                      <td className="text-slate-500 text-xs max-w-[200px] truncate">{m.notes || '\u2014'}</td>
                      <td className="text-xs text-slate-500">
                        {new Date(m.createdAt).toLocaleDateString('de-DE')}
                      </td>
                      <td className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => openEditForm(m)} className="sp-btn-icon" title="Bearbeiten">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => { if (confirm('Aufmaß wirklich löschen?')) deleteMutation.mutate(m.id); }}
                            className="sp-btn-icon hover:!text-error-500 hover:!bg-error-50"
                            title="Löschen"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
